import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/utils";
import * as shared from "@/styles/shared";
import api from "@shared/api/client";
import type { AddressSpending, ProviderSpending, SpendingPeriod } from "@shared/types/analytics";
import { formatCurrency } from "@shared/utils";
import { BarChart3, Clock, DollarSign, FileText } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as styles from "./AnalyticsPage.styles";
import { MonthlySpendingChart } from "./charts/MonthlySpendingChart";
import { PieLegendChart } from "./charts/PieLegendChart";

const AnalyticsPage = () => {
  const { t } = useTranslation(["common", "analytics"]);

  const [spending, setSpending] = useState<SpendingPeriod[]>([]);
  const [byProvider, setByProvider] = useState<ProviderSpending[]>([]);
  const [byAddress, setByAddress] = useState<AddressSpending[]>([]);
  const [statusDist, setStatusDist] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [spendingData, providerData, addressData, statusData] = await Promise.all([
        api.analytics.spending({ groupBy: "month" }),
        api.analytics.byProvider(),
        api.analytics.byAddress(),
        api.analytics.statusDistribution(),
      ]);
      setSpending(spendingData);
      setByProvider(providerData);
      setByAddress(addressData);
      setStatusDist(statusData);
    } catch {
      setSpending([]);
      setByProvider([]);
      setByAddress([]);
      setStatusDist({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const statusPieData = Object.entries(statusDist).map(([name, value]) => ({ name, value }));

  if (loading) {
    return (
      <div className={shared.page}>
        <div className={styles.loadingSkeleton} />
      </div>
    );
  }

  return (
    <div className={shared.page}>
      <PageHeader
        icon={BarChart3}
        title={t("analytics:pageTitle")}
        description={t("analytics:pageDescription", "Overview of your invoicing analytics")}
      />

      <div className={cn(styles.chartsGrid, styles.pageEnter)}>
        <Card variant="elevated" className={cn(styles.chartCard, styles.animateSlideUp)} style={{ animationDelay: "0ms" }}>
          <MonthlySpendingChart
            spending={spending}
            title={t("analytics:monthlySpending")}
            noDataLabel={t("empty.noSpendingData", "No spending data available")}
            noDataHint={t("empty.chartsHint", "Run scraping to generate data. Charts appear after the first import.")}
          />
        </Card>
        <Card variant="elevated" className={cn(styles.chartCard, styles.animateSlideUp)} style={{ animationDelay: "50ms" }}>
          <PieLegendChart
            data={byProvider}
            title={t("analytics:spendingByProvider")}
            noDataLabel={t("empty.noProviderData", "No provider data available")}
            noDataHint={t("empty.chartsHint", "Run scraping to generate data. Charts appear after the first import.")}
            icon={FileText}
            dataKey="total"
            nameKey="provider"
            label={(d) => d.provider}
            value={(d) => formatCurrency(d.total)}
          />
        </Card>
        <Card variant="elevated" className={cn(styles.chartCard, styles.animateSlideUp)} style={{ animationDelay: "100ms" }}>
          <PieLegendChart
            data={statusPieData}
            title={t("analytics:invoiceStatusDistribution")}
            noDataLabel={t("empty.noStatusData", "No status data available")}
            noDataHint={t("empty.chartsHint", "Run scraping to generate data. Charts appear after the first import.")}
            icon={Clock}
            dataKey="value"
            nameKey="name"
            label={(d) => d.name}
            value={(d) => d.value}
          />
        </Card>
        <Card variant="elevated" className={cn(styles.chartCard, styles.animateSlideUp)} style={{ animationDelay: "150ms" }}>
          <PieLegendChart
            data={byAddress}
            title={t("analytics:spendingByAddress")}
            noDataLabel={t("empty.noAddressData", "No address data available")}
            noDataHint={t("empty.chartsHint", "Run scraping to generate data. Charts appear after the first import.")}
            icon={DollarSign}
            dataKey="total"
            nameKey="address"
            label={(d) => d.address}
            value={(d) => formatCurrency(d.total)}
          />
        </Card>
      </div>
    </div>
  );
};

export default AnalyticsPage;
